import { Box, Paper, Typography, Avatar, CircularProgress } from '@mui/material';
import { ThumbUp, ThumbDown } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { submitFeedback } from '../services/apiClient'; // ✅ function in apiClient.js
import { useState, useRef, useEffect } from 'react';

import FeedbackModal from './FeedbackModal';


export default function ChatDisplay({ chatHistory, isLoading, clientCode, countryCode, contactId, conversationId, responseTime, logId }) {
    const [feedbackState, setFeedbackState] = useState({}); // {messageIndex: 'up'|'down'}
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

    const chatEndRef = useRef(null);

    // Scroll to bottom whenever chatHistory changes
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatHistory]);


    const handleFeedbackClick = async (index, like, extraFeedback = '') => {
        if (feedbackState[index]) return;

        const question = chatHistory[index - 1]?.content || '';
        const answer = chatHistory[index]?.content || '';

        const payload = {
            LogId : logId,
            ClientCode: clientCode,
            ConversationId: conversationId || `conv_${Date.now()}`,
            contactId: contactId || 'default_user',
            CountryCode: countryCode,
            like,
            question,
            answer,
            response_time: responseTime || null,
            extra_feedback: extraFeedback || null // 🔹 new field
        };

        try {
            await submitFeedback(payload);
            setFeedbackState(prev => ({ ...prev, [index]: like ? 'up' : 'down' }));
        } catch (e) {
            console.error("Failed to send feedback:", e);
        }
    };



    return (
        <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2, p: 2 }}>
            {chatHistory.map((msg, index) => (
                <Box 
                    key={index} 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: msg.role === 'user' ? 'flex-start' : 'flex-end', 
                        mb: 2 
                    }}
                >
                    <Paper 
                        elevation={1} 
                        sx={{ 
                            p: 2, 
                            borderRadius: 3, 
                            maxWidth: '80%',
                            bgcolor: msg.role === 'user' ? '#f1f1f1' : '#e8f5e9',
                            border: '1px solid',
                            borderColor: msg.role === 'user' ? '#ddd' : '#4a0072'
                        }}
                    >
                        {msg.role === 'assistant' ? (
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                        ) : (
                            <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
                                {msg.content}
                            </Typography>
                        )}
                        
                        {msg.role === 'assistant' && index === chatHistory.length - 1 && !msg.streaming && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee', display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ mr: 2 }}>Was this answer helpful?</Typography>

                                {feedbackState[index] === undefined && (
                                    <>
                                        <Avatar 
                                            sx={{ bgcolor: '#d0f0c0', width: 32, height: 32, mr: 1, cursor: 'pointer' }}
                                            onClick={() => handleFeedbackClick(index, true)}
                                        >
                                            <ThumbUp sx={{ color: '#4caf50' }} />
                                        </Avatar>
                                        <Avatar 
                                            sx={{ bgcolor: '#f8d7da', width: 32, height: 32, cursor: 'pointer' }}
                                            onClick={() => setIsFeedbackModalOpen(true)}
                                        >
                                            <ThumbDown sx={{ color: '#dc3545' }} />
                                        </Avatar>
                                        <FeedbackModal 
                                            open={isFeedbackModalOpen} 
                                            onClose={() => setIsFeedbackModalOpen(false)} 
                                            onSubmit={(text) => handleFeedbackClick(index, false, text)} 
                                        />
                                    </>
                                )}

                                {feedbackState[index] === 'up' && (
                                    <Avatar sx={{ bgcolor: '#d0f0c0', width: 32, height: 32 }}>
                                        <ThumbUp sx={{ color: '#4caf50' }} />
                                    </Avatar>
                                )}

                                {feedbackState[index] === 'down' && (
                                    <Avatar sx={{ bgcolor: '#f8d7da', width: 32, height: 32 }}>
                                        <ThumbDown sx={{ color: '#dc3545' }} />
                                    </Avatar>
                                )}
                            </Box>
                        )}

                    </Paper>
                </Box>
            ))}
            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <CircularProgress />
                </Box>
            )}
            {/* invisible element to scroll into view */}
            <div ref={chatEndRef} />
        </Box>
    );
}
